import {
  TASK_PROCEDURE_PARAM_ANCHOR_ID,
  TASK_PROCEDURE_PARAM_ANCHOR_TYPE,
  TASK_PROCEDURE_PARAM_INPUT_PREFIX,
} from '@erp/shared-types';

/** Select value: this parameter is not passed into the procedure. */
export const BINDING_UNMAPPED = '__unmapped__';
/** Select value: the parameter is a fixed string, edited in a text field. */
export const BINDING_LITERAL = '__literal__';
/** Select value: a `$.task.*` path that is not an input field or anchor. */
export const BINDING_CUSTOM = '__custom__';

export interface ProcedureInputField {
  fieldName: string;
  label: string;
}

export type ProcedureInputSchemaStatus = 'none' | 'loading' | 'error' | 'ready';

export interface DeclaredProcedureParam {
  name: string;
  description: string;
  required: boolean;
  /** The procedure supplies a value when the binding passes none. */
  hasDefault?: boolean;
}

export interface MappedProcedureParam {
  param: string;
  value: string;
}

export interface ProcedureParamRow {
  name: string;
  description: string;
  required: boolean;
  hasDefault?: boolean;
  /** Absent when the parameter is not mapped. */
  value?: string;
  declared: boolean;
}

export type ProcedureBindingKind = 'anchor' | 'input' | 'custom' | 'literal';

export function inputFieldBinding(fieldName: string): string {
  return `${TASK_PROCEDURE_PARAM_INPUT_PREFIX}${fieldName}`;
}

export function inputFieldNameFromBinding(value: string): string | null {
  if (!value.startsWith(TASK_PROCEDURE_PARAM_INPUT_PREFIX)) return null;
  const fieldName = value.slice(TASK_PROCEDURE_PARAM_INPUT_PREFIX.length);
  if (!fieldName || fieldName.includes('.') || fieldName.includes('[')) {
    return null;
  }
  return fieldName;
}

export function procedureBindingKind(value: string): ProcedureBindingKind {
  if (
    value === TASK_PROCEDURE_PARAM_ANCHOR_ID ||
    value === TASK_PROCEDURE_PARAM_ANCHOR_TYPE
  ) {
    return 'anchor';
  }
  if (inputFieldNameFromBinding(value)) return 'input';
  if (value.startsWith('$.task.')) return 'custom';
  return 'literal';
}

/** The select's value for one stored binding. Unmapped rows use the sentinel. */
export function bindingSelectValue(value: string | undefined): string {
  if (value == null) return BINDING_UNMAPPED;
  const kind = procedureBindingKind(value);
  if (kind === 'literal') return BINDING_LITERAL;
  if (kind === 'custom') return BINDING_CUSTOM;
  return value;
}

/**
 * Apply a select choice. `null` removes the mapping. Choosing literal or
 * custom keeps the current text when it is already that kind.
 */
export function valueAfterBindingChoice(
  current: string | undefined,
  choice: string,
): string | null {
  if (choice === BINDING_UNMAPPED) return null;
  if (choice === BINDING_LITERAL) {
    return current != null && procedureBindingKind(current) === 'literal'
      ? current
      : '';
  }
  if (choice === BINDING_CUSTOM) {
    return current != null && procedureBindingKind(current) === 'custom'
      ? current
      : '';
  }
  return choice;
}

/** Declared parameters first, then stored bindings the procedure no longer declares. */
export function procedureParamRows(
  declared: readonly DeclaredProcedureParam[],
  mapped: readonly MappedProcedureParam[],
): ProcedureParamRow[] {
  const byName = new Map(mapped.map((row) => [row.param, row.value]));
  const declaredNames = new Set(declared.map((param) => param.name));
  const rows: ProcedureParamRow[] = declared.map((param) => {
    const value = byName.get(param.name);
    return {
      name: param.name,
      description: param.description,
      required: param.required,
      hasDefault: param.hasDefault ?? false,
      declared: true,
      ...(value === undefined ? {} : { value }),
    };
  });
  for (const row of mapped) {
    if (declaredNames.has(row.param)) continue;
    rows.push({
      name: row.param,
      description: '',
      required: false,
      value: row.value,
      declared: false,
    });
  }
  return rows;
}

export function setProcedureParamValue(
  mapped: readonly MappedProcedureParam[],
  name: string,
  value: string | null,
): MappedProcedureParam[] {
  if (value == null) return mapped.filter((row) => row.param !== name);
  const index = mapped.findIndex((row) => row.param === name);
  if (index === -1) return [...mapped, { param: name, value }];
  return mapped.map((row) =>
    row.param === name ? { param: name, value } : row,
  );
}

/** A parameter as the procedure declares it. */
type ProcedureParameterSource = {
  name: string;
  description: string;
  required: boolean;
  default?: string | null;
};

export type ProcedureMapperPhase = 'choose' | 'loading' | 'empty' | 'list';

export function waitingForPinnedVersion(
  pinnedVersionId: string | null,
  versionsLoading: boolean,
): boolean {
  return Boolean(pinnedVersionId) && versionsLoading;
}

export function pinnedProcedureVersion<T extends { id: string }>(
  versions: readonly T[] | undefined,
  pinnedVersionId: string | null,
): T | undefined {
  if (!pinnedVersionId || !versions) return undefined;
  return versions.find((version) => version.id === pinnedVersionId);
}

/** Skip the procedure fetch until a pinned version number is known. */
export function sopQueryId(input: {
  sopId: string;
  waitingForPin: boolean;
}): string | undefined {
  if (!input.sopId || input.waitingForPin) return undefined;
  return input.sopId;
}

export function procedureParameterView(input: {
  waiting: boolean;
  loadFailed: boolean;
  mapped: readonly MappedProcedureParam[];
  parameters: readonly ProcedureParameterSource[] | undefined;
}) {
  const declared = input.waiting
    ? []
    : declaredProcedureParams(input.parameters);
  const rows = procedureParamRows(declared, input.mapped);
  return {
    waiting: input.waiting,
    loadFailed: input.loadFailed,
    mapped: input.mapped,
    declared,
    rows,
    empty: procedureMappingIsEmpty({
      loadFailed: input.loadFailed,
      declaredCount: declared.length,
      mappedCount: input.mapped.length,
    }),
  };
}

function declaredProcedureParams(
  parameters: readonly ProcedureParameterSource[] | undefined,
): DeclaredProcedureParam[] {
  return (parameters ?? []).map((param) => ({
    name: param.name,
    description: param.description,
    required: param.required,
    hasDefault: param.default != null,
  }));
}

export function procedureParamsAreLoading(input: {
  sopId: string;
  waitingForPin: boolean;
  sopLoading: boolean;
}): boolean {
  if (!input.sopId || input.waitingForPin) return input.waitingForPin;
  return input.sopLoading;
}

export function procedureMappingIsEmpty(input: {
  loadFailed: boolean;
  declaredCount: number;
  mappedCount: number;
}): boolean {
  if (input.loadFailed) return false;
  return input.declaredCount === 0 && input.mappedCount === 0;
}

export function procedureMapperPhase(input: {
  sopId: string;
  waiting: boolean;
  empty: boolean;
}): ProcedureMapperPhase {
  if (!input.sopId) return 'choose';
  if (input.waiting) return 'loading';
  if (input.empty) return 'empty';
  return 'list';
}

export function rowsHaveInputBinding(
  rows: readonly ProcedureParamRow[],
): boolean {
  return rows.some(
    (row) => row.value != null && procedureBindingKind(row.value) === 'input',
  );
}

/**
 * Required parameters a task of this type cannot fill: declared, not mapped,
 * and without a default. Only reported once the type's input schema is
 * loaded, since the fix is to map them onto its fields.
 */
export function unmappedRequiredParams(
  rows: readonly ProcedureParamRow[],
  status: ProcedureInputSchemaStatus,
): string[] {
  if (status !== 'ready') return [];
  const names: string[] = [];
  for (const row of rows) {
    if (row.declared && rowNeedsTaskValue(row)) names.push(row.name);
  }
  return names;
}

/** Required, not mapped, and with no default for the procedure to fall back on. */
export function rowNeedsTaskValue(row: ProcedureParamRow): boolean {
  return row.required && !row.hasDefault && row.value == null;
}

export function missingSchemaNotice(input: {
  status: ProcedureInputSchemaStatus;
  hasDeclared: boolean;
  hasInputBinding: boolean;
}): boolean {
  if (input.status !== 'none') return false;
  return input.hasDeclared || input.hasInputBinding;
}

export function inputFieldOptionLabel(field: ProcedureInputField): string {
  if (field.label === field.fieldName) return field.label;
  return `${field.label} (${field.fieldName})`;
}

/** A bound `$.task.input` field that is not on the selected schema. */
export function unknownInputField(
  value: string | undefined,
  fieldNames: ReadonlySet<string>,
): string | null {
  if (value == null) return null;
  const fieldName = inputFieldNameFromBinding(value);
  if (fieldName == null || fieldNames.has(fieldName)) return null;
  return fieldName;
}

export function showInputFieldGroup(
  fieldCount: number,
  unknownField: string | null,
): boolean {
  return fieldCount > 0 || unknownField != null;
}

export function parameterTextMode(
  value: string | undefined,
): 'literal' | 'custom' | null {
  if (value == null) return null;
  const kind = procedureBindingKind(value);
  if (kind === 'literal' || kind === 'custom') return kind;
  return null;
}

export function applyNameMatches(
  mapped: readonly MappedProcedureParam[],
  matches: readonly MappedProcedureParam[],
): MappedProcedureParam[] {
  return matches.reduce(
    (current, match) =>
      setProcedureParamValue(current, match.param, match.value),
    [...mapped],
  );
}

/** Unmapped parameters whose name matches an input field. */
export function matchUnmappedParamsByFieldName(
  rows: readonly ProcedureParamRow[],
  fields: readonly ProcedureInputField[],
): MappedProcedureParam[] {
  const names = new Set(fields.map((field) => field.fieldName));
  return rows.flatMap((row) =>
    row.value == null && names.has(row.name)
      ? [{ param: row.name, value: inputFieldBinding(row.name) }]
      : [],
  );
}
